import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CapitalProvider,
  CapitalRequest,
  CapitalRequestInput,
  CapitalRequestMatch,
  RequestMessage,
  RequestQuestion,
} from '@scaleup/models';
import { baseQuery } from './baseQuery';
import { TagTypes } from './tagTypes';

export const capitalRequestApi = createApi({
  reducerPath: 'capitalRequestApi',
  baseQuery: baseQuery,
  tagTypes: [TagTypes.capitalRequests],
  endpoints: (builder) => ({
    getCapitalRequests: builder.query<CapitalRequest[], void>({
      query: () => '/capital_request',
      providesTags: [TagTypes.capitalRequests],
    }),
    createCapitalRequest: builder.mutation<void, CapitalRequestInput>({
      query: (capitalRequestInput) => ({
        url: '/capital_request',
        method: 'POST',
        body: capitalRequestInput,
      }),
      invalidatesTags: [TagTypes.capitalRequests],
    }),
    saveCapitalRequestDraft: builder.mutation<void, CapitalRequestInput>({
      query: (capitalRequestInput) => ({
        url: '/capital_request',
        method: 'POST',
        body: { ...capitalRequestInput, draft: true },
      }),
      invalidatesTags: [TagTypes.capitalRequests],
    }),
    getCrQuestions: builder.query<RequestQuestion[], string>({
      query: (id) => `/capital_request/${id}/question`,
    }),
    answerCrQuestion: builder.mutation<
      void,
      {
        id: string;
        questionId: string;
        answer: string;
      }
    >({
      query: ({ id, questionId, answer }) => ({
        url: `/capital_request/${id}/question/${questionId}`,
        method: 'POST',
        body: {
          answer,
        },
      }),
      invalidatesTags: [TagTypes.capitalRequests],
    }),
    getCrMatches: builder.query<CapitalRequestMatch[], string>({
      query: (id) => `/capital_request/${id}/match`,
    }),
    getCrCapitalProvider: builder.query<
      CapitalProvider,
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) =>
        `/capital_request/${id}/match/${matchId}/capital-provider`,
    }),
    getCrMessages: builder.query<
      RequestMessage[],
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) =>
        `/capital_request/${id}/match/${matchId}/message`,
    }),
    createCrMessage: builder.mutation<
      void,
      { id: string; matchId: string; message: string }
    >({
      query: ({ id, matchId, message }) => ({
        url: `/capital_request/${id}/match/${matchId}/message`,
        method: 'POST',
        body: {
          message,
        },
      }),
      invalidatesTags: [TagTypes.capitalRequests],
    }),
    acceptDeclineCrm: builder.mutation<
      void,
      { id: string; matchId: string; accept: boolean }
    >({
      query: ({ id, matchId, accept }) => ({
        url: `/capital_request/${id}/match/${matchId}`,
        method: 'POST',
        body: {
          accept,
        },
      }),
      invalidatesTags: [TagTypes.capitalRequests],
    }),
    crDealStatus: builder.mutation<void, { id: string; dealWon: boolean }>({
      query: ({ id, dealWon }) => ({
        url: `/capital_request/${id}`,
        method: 'POST',
        body: {
          dealWon,
        },
      }),
      invalidatesTags: [TagTypes.capitalRequests],
    }),
  }),
});

export const {
  useGetCapitalRequestsQuery,
  useCreateCapitalRequestMutation,
  useSaveCapitalRequestDraftMutation,
  useGetCrQuestionsQuery,
  useAnswerCrQuestionMutation,
  useGetCrMatchesQuery,
  useGetCrCapitalProviderQuery,
  useLazyGetCrMessagesQuery,
  useCreateCrMessageMutation,
  useAcceptDeclineCrmMutation,
  useCrDealStatusMutation,
} = capitalRequestApi;
